import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { adminApi } from "../api/adminApi";
import DatePicker, { nextDay } from "./DatePicker";
import MonthBar from "./MonthBar";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const emptyBulk = () => ({ fromDate: "", toDate: "", adultPrice: "", childPrice: "", costAdultPrice: "", costChildPrice: "", roomPrice: "", extraAdultPrice: "", extraBedPrice: "", childBandPrices: [] });
const bandLabel = (b, i) => b?.label || (b?.maxAge != null && b?.maxAge !== "" ? `≤${b.maxAge}` : `Band ${i + 1}`);

const RatePlanPrices = ({ ratePlanId, ratePlanName, onClose }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [nights, setNights] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [single, setSingle] = useState(null);
  const [bulk, setBulk] = useState(emptyBulk());
  const [toast, setToast] = useState(null);
  const [singleErr, setSingleErr] = useState({});

  const flash = (type, text) => setToast({ type, text });
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2200); return () => clearTimeout(t); }, [toast]);

  const first = useMemo(() => new Date(month.getFullYear(), month.getMonth(), 1), [month]);
  const last = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 0), [month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi("GET", `/admin/inventory/rate-plan-prices?ratePlanId=${ratePlanId}&fromDate=${ymd(first)}&toDate=${ymd(last)}`);
      setNights(res?.data?.data?.nights || []);
      setPlan(res?.data?.data?.ratePlan || null);
    } finally {
      setLoading(false);
    }
  }, [ratePlanId, first, last]);
  useEffect(() => { load(); }, [load]);

  const perRoom = plan?.pricingMode === "per_room";
  const bands = useMemo(() => (Array.isArray(plan?.childBands) ? plan.childBands : []), [plan]);
  const byDate = useMemo(() => new Map(nights.map((n) => [n.date, n])), [nights]);

  const pickDay = (dateStr) => {
    const n = byDate.get(dateStr);
    setSelected(dateStr);
    setSingleErr({});
    setSingle({
      adultPrice: n?.adultPrice ?? "",
      childPrice: n?.childPrice ?? "",
      costAdultPrice: n?.costAdultPrice ?? "",
      costChildPrice: n?.costChildPrice ?? "",
      roomPrice: n?.roomPrice ?? "",
      extraAdultPrice: n?.extraAdultPrice ?? "",
      extraBedPrice: n?.extraBedPrice ?? "",
      childBandPrices: bands.map((_, i) => (Array.isArray(n?.childBandPrices) ? (n.childBandPrices[i] ?? "") : "")),
    });
  };

  const singleValues = () => {
    if (perRoom) {
      return {
        roomPrice: single.roomPrice,
        extraAdultPrice: single.extraAdultPrice,
        extraBedPrice: single.extraBedPrice,
        childBandPrices: (single.childBandPrices || []).map((v) => Number(v) || 0),
      };
    }
    return { adultPrice: single.adultPrice, childPrice: single.childPrice, costAdultPrice: single.costAdultPrice, costChildPrice: single.costChildPrice };
  };

  const saveSingle = async () => {
    const errs = {};
    if (perRoom) {
      if (!(Number(single.roomPrice) > 0)) errs.roomPrice = true;
    } else {
      if (!(Number(single.adultPrice) > 0)) errs.adultPrice = true;
      if (!(Number(single.childPrice) > 0)) errs.childPrice = true;
    }
    if (Object.keys(errs).length) {
      setSingleErr(errs);
      flash("err", perRoom ? "Room price must be greater than 0" : "Adult and child price must be greater than 0");
      return;
    }
    setSingleErr({});
    try {
      await adminApi("POST", "/admin/inventory/rate-plan-prices", { ratePlanId, date: selected, values: singleValues() });
      flash("ok", "Saved");
      setSelected(null);
      setSingle(null);
      load();
    } catch (e) {
      flash("err", e?.response?.data?.message || "Save failed");
    }
  };

  const applyBulk = async () => {
    if (!bulk.fromDate || !bulk.toDate) { flash("err", "Pick a date range"); return; }
    const values = {};
    if (perRoom) {
      ["roomPrice", "extraAdultPrice", "extraBedPrice"].forEach((k) => { if (bulk[k] !== "") values[k] = bulk[k]; });
      const bp = bulk.childBandPrices || [];
      if (bands.length && bp.some((v) => v !== "" && v != null)) values.childBandPrices = bands.map((_, i) => Number(bp[i]) || 0);
    } else {
      ["adultPrice", "childPrice", "costAdultPrice", "costChildPrice"].forEach((k) => { if (bulk[k] !== "") values[k] = bulk[k]; });
    }
    if (!Object.keys(values).length) { flash("err", "Enter at least one price"); return; }
    if (perRoom) {
      if (values.roomPrice != null && !(Number(values.roomPrice) > 0)) { flash("err", "Room price must be greater than 0"); return; }
    } else {
      if (values.adultPrice != null && !(Number(values.adultPrice) > 0)) { flash("err", "Adult price must be greater than 0"); return; }
      if (values.childPrice != null && !(Number(values.childPrice) > 0)) { flash("err", "Child price must be greater than 0"); return; }
    }
    try {
      await adminApi("POST", "/admin/inventory/rate-plan-prices", { ratePlanId, fromDate: bulk.fromDate, toDate: bulk.toDate, values });
      flash("ok", "Range updated");
      setBulk(emptyBulk());
      load();
    } catch (e) {
      flash("err", e?.response?.data?.message || "Update failed");
    }
  };

  const setBulkBand = (i, v) => setBulk((b) => { const arr = [...(b.childBandPrices || [])]; arr[i] = v; return { ...b, childBandPrices: arr }; });
  const setSingleBand = (i, v) => setSingle((s) => { const arr = [...(s.childBandPrices || [])]; arr[i] = v; return { ...s, childBandPrices: arr }; });

  const cells = useMemo(() => {
    const lead = first.getDay();
    const total = last.getDate();
    const arr = [];
    for (let i = 0; i < lead; i += 1) arr.push(null);
    for (let day = 1; day <= total; day += 1) arr.push(ymd(new Date(month.getFullYear(), month.getMonth(), day)));
    return arr;
  }, [first, last, month]);

  return (
    <div className="hi-modal-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="hi-modal">
        {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}
        <div className="hi-modal-head">
          <h3>Nightly prices — {ratePlanName} <HelpTip title="Setting nightly prices">
            <p>This calendar holds the selling price for each night of this rate plan.</p>
            <ul>
              <li><strong>Click one day</strong> to edit just that night.</li>
              <li><strong>Bulk edit</strong> paints a whole date range at once — ideal for a season (e.g. 01 Dec–10 Jan). Set the range, type the prices, apply.</li>
              <li><strong>Per-person</strong> plans ask for adult &amp; child price. <strong>Per-room</strong> plans ask for the flat room price plus extra-adult, extra-bed and child-band prices.</li>
              <li>A night with no price is simply not sellable — you must price <em>and</em> open availability for a night to sell.</li>
            </ul>
          </HelpTip> <span className={`hi-mode-badge${perRoom ? " room" : ""}`}>{perRoom ? "Per-room pricing" : "Per-person pricing"}</span></h3>
          <button className="hi-x" onClick={onClose}>×</button>
        </div>
        <div className="hi-modal-body">
          <div className="hi-sub" style={{ marginBottom: 10 }}>
            {perRoom
              ? "Per-room rate: flat room price + extra person/bed and per-age-band child prices. Click a day to edit it, or use bulk edit below."
              : "Adult & child price per night — this rate plan only. Click a day to edit it, or use bulk edit below."}
            {" "}<span style={{ color: "#98a2b6" }}>To change this, set the Pricing model in the rate plan and Save.</span>
          </div>

          <MonthBar month={month} onChange={setMonth} />

          {loading ? (
            <div className="hi-empty">Loading…</div>
          ) : (
            <div className="hi-cal7">
              {DOW.map((d) => <div className="hi-dow" key={d}>{d}</div>)}
              {cells.map((dateStr, i) => {
                if (!dateStr) return <div className="hi-daycell blank" key={`b${i}`} />;
                const n = byDate.get(dateStr);
                const day = Number(dateStr.slice(-2));
                return (
                  <div className={`hi-daycell${selected === dateStr ? " is-sel" : ""}`} key={dateStr} onClick={() => pickDay(dateStr)}>
                    <div className="dn">{day}</div>
                    {perRoom ? (
                      n && n.roomPrice ? (
                        <>
                          <div className="av">R {n.roomPrice || 0}</div>
                          {n.extraAdultPrice ? <div className="pr">+A {n.extraAdultPrice}</div> : null}
                        </>
                      ) : <div className="pr">—</div>
                    ) : (
                      n && (n.adultPrice || n.childPrice) ? (
                        <>
                          <div className="av">A {n.adultPrice || 0}</div>
                          <div className="pr">C {n.childPrice || 0}</div>
                        </>
                      ) : <div className="pr">—</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selected && single && (
            <div className="hi-section" style={{ marginTop: 16 }}>
              <h3>Edit {selected}</h3>
              {perRoom ? (
                <>
                  <div className="hi-grid three">
                    <div className="hi-fld"><label>Room price *</label><input className={`inp${singleErr.roomPrice ? " err" : ""}`} type="number" value={single.roomPrice} onChange={(e) => { setSingle({ ...single, roomPrice: e.target.value }); setSingleErr((x) => ({ ...x, roomPrice: false })); }} />{singleErr.roomPrice ? <span className="hi-err">Room price must be greater than 0</span> : null}</div>
                    <div className="hi-fld"><label>Extra adult</label><input className="inp" type="number" value={single.extraAdultPrice} onChange={(e) => setSingle({ ...single, extraAdultPrice: e.target.value })} /></div>
                    <div className="hi-fld"><label>Extra bed</label><input className="inp" type="number" value={single.extraBedPrice} onChange={(e) => setSingle({ ...single, extraBedPrice: e.target.value })} /></div>
                  </div>
                  {bands.length ? (
                    <>
                      <div className="hi-bulk-sub">Child prices by age band</div>
                      <div className="hi-grid three">
                        {bands.map((b, i) => (
                          <div className="hi-fld" key={i}><label>Child {bandLabel(b, i)}</label><input className="inp" type="number" value={single.childBandPrices?.[i] ?? ""} onChange={(e) => setSingleBand(i, e.target.value)} /></div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </>
              ) : (
                <div className="hi-grid">
                  <div className="hi-fld"><label>Adult price *</label><input className={`inp${singleErr.adultPrice ? " err" : ""}`} type="number" value={single.adultPrice} onChange={(e) => { setSingle({ ...single, adultPrice: e.target.value }); setSingleErr((x) => ({ ...x, adultPrice: false })); }} />{singleErr.adultPrice ? <span className="hi-err">Adult price must be greater than 0</span> : null}</div>
                  <div className="hi-fld"><label>Child price *</label><input className={`inp${singleErr.childPrice ? " err" : ""}`} type="number" value={single.childPrice} onChange={(e) => { setSingle({ ...single, childPrice: e.target.value }); setSingleErr((x) => ({ ...x, childPrice: false })); }} />{singleErr.childPrice ? <span className="hi-err">Child price must be greater than 0</span> : null}</div>
                  <div className="hi-fld"><label>Cost adult price</label><input className="inp" type="number" value={single.costAdultPrice} onChange={(e) => setSingle({ ...single, costAdultPrice: e.target.value })} /></div>
                  <div className="hi-fld"><label>Cost child price</label><input className="inp" type="number" value={single.costChildPrice} onChange={(e) => setSingle({ ...single, costChildPrice: e.target.value })} /></div>
                </div>
              )}
              <div className="hi-inline" style={{ marginTop: 12, justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={() => { setSelected(null); setSingle(null); }}>Cancel</button>
                <button className="btn primary" onClick={saveSingle}>Save day</button>
              </div>
            </div>
          )}

          <div className="hi-section" style={{ marginTop: 16 }}>
            <h3>Bulk edit (date range)</h3>
            <div className="hi-grid two">
              <div className="hi-fld"><label>From</label><DatePicker value={bulk.fromDate} onChange={(v) => setBulk((b) => ({ ...b, fromDate: v, toDate: (!b.toDate || b.toDate <= v) ? nextDay(v) : b.toDate }))} /></div>
              <div className="hi-fld"><label>To</label><DatePicker value={bulk.toDate} min={bulk.fromDate} onChange={(v) => setBulk({ ...bulk, toDate: v })} /></div>
            </div>
            {perRoom ? (
              <>
                <div className="hi-grid three" style={{ marginTop: 12 }}>
                  <div className="hi-fld"><label>Room price</label><input className="inp" type="number" value={bulk.roomPrice} onChange={(e) => setBulk({ ...bulk, roomPrice: e.target.value })} /></div>
                  <div className="hi-fld"><label>Extra adult</label><input className="inp" type="number" value={bulk.extraAdultPrice} onChange={(e) => setBulk({ ...bulk, extraAdultPrice: e.target.value })} /></div>
                  <div className="hi-fld"><label>Extra bed</label><input className="inp" type="number" value={bulk.extraBedPrice} onChange={(e) => setBulk({ ...bulk, extraBedPrice: e.target.value })} /></div>
                </div>
                {bands.length ? (
                  <>
                    <div className="hi-bulk-sub">Child prices by age band</div>
                    <div className="hi-grid three">
                      {bands.map((b, i) => (
                        <div className="hi-fld" key={i}><label>Child {bandLabel(b, i)}</label><input className="inp" type="number" value={bulk.childBandPrices?.[i] ?? ""} onChange={(e) => setBulkBand(i, e.target.value)} /></div>
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className="hi-grid" style={{ marginTop: 12 }}>
                <div className="hi-fld"><label>Adult price</label><input className="inp" type="number" value={bulk.adultPrice} onChange={(e) => setBulk({ ...bulk, adultPrice: e.target.value })} /></div>
                <div className="hi-fld"><label>Child price</label><input className="inp" type="number" value={bulk.childPrice} onChange={(e) => setBulk({ ...bulk, childPrice: e.target.value })} /></div>
                <div className="hi-fld"><label>Cost adult price</label><input className="inp" type="number" value={bulk.costAdultPrice} onChange={(e) => setBulk({ ...bulk, costAdultPrice: e.target.value })} /></div>
                <div className="hi-fld"><label>Cost child price</label><input className="inp" type="number" value={bulk.costChildPrice} onChange={(e) => setBulk({ ...bulk, costChildPrice: e.target.value })} /></div>
              </div>
            )}
            <div className="hi-inline" style={{ marginTop: 12, justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={applyBulk}>Apply to range</button>
            </div>
          </div>
        </div>
        <div className="hi-modal-foot">
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default RatePlanPrices;
